import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, ExternalLink, MessageSquare } from "lucide-react";

const STATUSES = ["new", "shortlisted", "contacted", "selected", "rejected"];
const STATUS_COLOR = {
  new: "text-[#C5A059]",
  shortlisted: "text-[#7BC46B]",
  contacted: "text-[#F4E4BC]",
  selected: "text-[#7BC46B]",
  rejected: "text-[#B75454]",
};

export default function AdminCastingApplicationsTab() {
  const [apps, setApps] = useState([]);
  const [calls, setCalls] = useState([]);
  const [filter, setFilter] = useState({ casting_id: "", status: "", category: "", q: "" });
  const [expanded, setExpanded] = useState(null);
  const [noteText, setNoteText] = useState("");

  const load = () => {
    const params = Object.fromEntries(Object.entries(filter).filter(([, v]) => v));
    api.get("/admin/casting-applications", { params }).then((r) => setApps(r.data)).catch(() => {});
  };

  useEffect(() => { api.get("/admin/casting-calls").then((r) => setCalls(r.data)).catch(() => {}); }, []);
  useEffect(load, [filter.casting_id, filter.status, filter.category, filter.q]);

  const categories = useMemo(() => {
    const s = new Set();
    apps.forEach((a) => a.category && s.add(a.category));
    return [...s];
  }, [apps]);

  const changeStatus = async (application_id, status, note) => {
    try {
      await api.post("/admin/casting-applications/status", { application_id, status, note });
      toast.success(`Marked as ${status}`);
      setNoteText("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="sv-card p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative">
          <Search size={14} className="absolute top-1/2 -translate-y-1/2 left-3 text-[#8A8478]" />
          <input placeholder="Search name / email / phone / city" className="sv-input text-xs pl-9"
            value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} data-testid="ca-search" />
        </div>
        <select className="sv-select text-xs" value={filter.casting_id} onChange={(e) => setFilter({ ...filter, casting_id: e.target.value })}>
          <option value="">All casting calls</option>
          {calls.map((c) => <option key={c.casting_id} value={c.casting_id}>{c.project} — {c.role}</option>)}
        </select>
        <select className="sv-select text-xs" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="sv-select text-xs" value={filter.category} onChange={(e) => setFilter({ ...filter, category: e.target.value })}>
          <option value="">All categories</option>
          {categories.map((c) => <option key={c}>{c}</option>)}
        </select>
      </div>

      {apps.length === 0 && <div className="text-sm text-[#8A8478] px-2">No casting applications match your filters yet.</div>}

      <div className="space-y-3">
        {apps.map((a) => (
          <div key={a.application_id} className="sv-card p-5" data-testid={`ca-row-${a.application_id}`}>
            <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
              <div className="flex gap-4 items-center min-w-0">
                {a.photo_url ? (
                  <img src={a.photo_url} className="w-16 h-16 rounded object-cover flex-shrink-0" alt="" />
                ) : (
                  <div className="w-16 h-16 rounded bg-[#1A1A1F] grid place-items-center font-mono-label text-xs text-[#C5A059] flex-shrink-0">
                    {a.full_name?.slice(0, 2).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-serif-editorial text-lg text-[#F4EFE4] truncate">{a.full_name}</div>
                  <div className="text-xs text-[#8A8478] truncate">{a.email} · {a.phone} · {a.city}</div>
                  <div className="text-[10px] font-mono-label mt-1">
                    <span className="text-[#C5A059]">{a.casting_project} — {a.casting_role}</span>
                    <span className={`ml-3 ${STATUS_COLOR[a.status] || "text-[#8A8478]"}`}>● {a.status?.toUpperCase()}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                {STATUSES.filter((s) => s !== a.status).map((s) => (
                  <button key={s} onClick={() => changeStatus(a.application_id, s)}
                    className="sv-btn-outline text-[10px] uppercase tracking-wider" data-testid={`ca-status-${a.application_id}-${s}`}>
                    → {s}
                  </button>
                ))}
                <button onClick={() => setExpanded(expanded === a.application_id ? null : a.application_id)}
                  className="sv-btn-outline text-[10px] uppercase tracking-wider inline-flex items-center gap-1">
                  <MessageSquare size={11} />{expanded === a.application_id ? "Hide" : "Details"}
                </button>
              </div>
            </div>

            {expanded === a.application_id && (
              <div className="mt-5 pt-5 border-t border-[rgba(255,255,255,0.06)] grid md:grid-cols-2 gap-4 text-sm">
                <div className="space-y-1 text-[#CDC7BA]">
                  <div><span className="text-[#8A8478] text-xs">Age:</span> {a.age || "-"}</div>
                  <div><span className="text-[#8A8478] text-xs">Gender:</span> {a.gender || "-"}</div>
                  <div><span className="text-[#8A8478] text-xs">Height:</span> {a.height || "-"}</div>
                  <div><span className="text-[#8A8478] text-xs">Category:</span> {a.category || "-"}</div>
                  <div><span className="text-[#8A8478] text-xs">Experience:</span> {a.experience || "-"}</div>
                  <div><span className="text-[#8A8478] text-xs">Instagram:</span> {a.instagram || "-"}</div>
                  {a.portfolio_url && <div><a href={a.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-[#C5A059] inline-flex items-center gap-1 text-xs"><ExternalLink size={11} />Portfolio</a></div>}
                  {a.video_url && <div><a href={a.video_url} target="_blank" rel="noopener noreferrer" className="text-[#C5A059] inline-flex items-center gap-1 text-xs"><ExternalLink size={11} />Video</a></div>}
                  {a.photo_url && <div><a href={a.photo_url} target="_blank" rel="noopener noreferrer" className="text-[#C5A059] inline-flex items-center gap-1 text-xs"><ExternalLink size={11} />Full-res photo</a></div>}
                </div>
                <div>
                  {a.message && <div className="text-[#CDC7BA] italic text-sm mb-4">"{a.message}"</div>}
                  <div className="font-mono-label text-[10px] text-[#C5A059] mb-2">Internal Notes</div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {(a.notes || []).map((n, i) => (
                      <div key={i} className="text-xs text-[#CDC7BA] bg-[#0F0F12] rounded p-2">
                        <div className="text-[#8A8478] text-[10px]">{n.by} · {n.at?.slice(0, 16)}</div>
                        <div>{n.text}</div>
                      </div>
                    ))}
                    {(a.notes || []).length === 0 && <div className="text-xs text-[#8A8478]">No notes yet.</div>}
                  </div>
                  <div className="mt-3 flex gap-2">
                    <input placeholder="Add internal note…" className="sv-input text-xs flex-1"
                      value={noteText} onChange={(e) => setNoteText(e.target.value)}
                      data-testid={`ca-note-${a.application_id}`} />
                    <button onClick={() => noteText && changeStatus(a.application_id, a.status, noteText)}
                      className="sv-btn-outline text-[10px]">Add Note</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
